import { Controller } from "@hotwired/stimulus";
import Sortable from "sortablejs";
import * as Turbo from "@hotwired/turbo";
import { handleStaleResponse } from "../lib/handle_stale_response";
import { autosaveDraft, cancel as cancelAutosave } from "../components/lib/autosave_draft";

export default class extends Controller {
  static targets = ["bank", "slot", "dropZone", "imageArea", "chip", "checkButton"];
  static values = {
    group: String,
    checkUrl: String,
    draftUrl: String,
    selectedChipAria: String,
  };

  connect() {
    this.selectedChip = null;
    this.checking = false;
    this.sortables = [];
    this._shuffleBank();
    this._initSortables();
    this._refreshState();
  }

  disconnect() {
    this.sortables.forEach((s) => s.destroy());
    this.sortables = [];
    if (this.hasDraftUrlValue) cancelAutosave(this.draftUrlValue);
  }

  _shuffleBank() {
    if (!this.hasBankTarget) return;
    const chips = Array.from(this.bankTarget.children);
    for (let i = chips.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      this.bankTarget.insertBefore(chips[j], chips[i]);
      [chips[i], chips[j]] = [chips[j], chips[i]];
    }
  }

  _initSortables() {
    if (this.hasBankTarget) {
      this.sortables.push(
        Sortable.create(this.bankTarget, {
          group: this.groupValue,
          animation: 150,
          ghostClass: "fill-blank-chip--ghost",
          chosenClass: "fill-blank-chip--chosen",
          draggable: ".fill-blank-chip",
          filter: ".fill-blank-chip--correct",
          onEnd: () => this._afterMove(),
        }),
      );
    }

    this.slotTargets.forEach((slot) => {
      if (slot.dataset.locked === "true") return;
      const dropZone = slot.querySelector("[data-word-image-match-player-target='dropZone']");
      if (!dropZone) return;

      this.sortables.push(
        Sortable.create(dropZone, {
          group: this.groupValue,
          animation: 150,
          ghostClass: "fill-blank-chip--ghost",
          chosenClass: "fill-blank-chip--chosen",
          draggable: ".fill-blank-chip",
          filter: ".fill-blank-chip--correct",
          onAdd: (event) => this._onChipAddedToSlot(event, dropZone),
          onEnd: () => this._afterMove(),
        }),
      );

      // Forwarder: drops on the image area get redirected to the slot's drop zone
      const imageArea = slot.querySelector("[data-word-image-match-player-target='imageArea']");
      if (imageArea) {
        this.sortables.push(
          Sortable.create(imageArea, {
            group: this.groupValue,
            animation: 0,
            ghostClass: "fill-blank-chip--ghost",
            chosenClass: "fill-blank-chip--chosen",
            draggable: ".fill-blank-chip",
            filter: ".fill-blank-chip--correct, img",
            onAdd: (event) => {
              dropZone.appendChild(event.item);
              this._onChipAddedToSlot({ item: event.item, from: event.from }, dropZone);
              this._afterMove();
            },
          }),
        );
      }
    });
  }

  // Bank chip clicked: select / deselect
  // Slot chip clicked: return chip to bank (unless correct)
  chipClicked(event) {
    const chip = event.currentTarget;
    if (chip.classList.contains("fill-blank-chip--correct")) return;
    event.preventDefault();
    event.stopPropagation();

    if (this.bankTarget.contains(chip)) {
      // toggle selection
      if (this.selectedChip === chip) {
        this._deselectChip();
        return;
      }
      if (this.selectedChip) this._deselectChip();
      this.selectedChip = chip;
      chip.classList.add("fill-blank-chip--selected");
      chip.setAttribute("aria-pressed", "true");
      if (this.hasSelectedChipAriaValue) {
        if (!chip.dataset.originalLabel) {
          chip.dataset.originalLabel = chip.getAttribute("aria-label") || "";
        }
        chip.setAttribute("aria-label", this.selectedChipAriaValue.replace("%{word}", chip.textContent.trim()));
      }
      // Move keyboard focus to first empty unlocked slot
      const firstOpen = this.slotTargets.find(
        (s) => s.dataset.locked !== "true" && s.dataset.state !== "correct" && !this._slotChip(s),
      );
      if (firstOpen) firstOpen.focus();
    } else {
      // Chip in a slot: send back to bank
      const slot = chip.closest("[data-word-image-match-player-target='slot']");
      if (slot && slot.dataset.locked === "true") return;
      this._returnChipToBank(chip, slot);
      this._afterMove();
    }
  }

  chipKeydown(event) {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    this.chipClicked(event);
  }

  slotClicked(event) {
    const slot = event.currentTarget;
    if (slot.dataset.locked === "true") return;
    if (slot.dataset.state === "correct") return;

    // Ignore clicks that originated on a chip (handled by chipClicked)
    if (event.target.closest(".fill-blank-chip")) return;

    event.preventDefault();
    const dropZone = this._dropZoneOf(slot);
    if (!dropZone) return;

    const occupant = this._slotChip(slot);

    if (this.selectedChip && !occupant) {
      const chip = this.selectedChip;
      this._deselectChip();
      this._placeChipInSlot(chip, dropZone);
    } else if (this.selectedChip && occupant && !occupant.classList.contains("fill-blank-chip--correct")) {
      const chip = this.selectedChip;
      this._deselectChip();
      this._returnChipToBank(occupant, slot);
      this._placeChipInSlot(chip, dropZone);
    } else if (!this.selectedChip && occupant && !occupant.classList.contains("fill-blank-chip--correct")) {
      this._returnChipToBank(occupant, slot);
    }
    this._afterMove();
  }

  slotKeydown(event) {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    this.slotClicked(event);
  }

  _onChipAddedToSlot(event, dropZone) {
    const slot = dropZone.closest("[data-word-image-match-player-target='slot']");
    if (slot.dataset.locked === "true") {
      event.from.appendChild(event.item);
      return;
    }
    const chips = dropZone.querySelectorAll(".fill-blank-chip");
    if (chips.length > 1) {
      chips.forEach((chip) => {
        if (chip !== event.item && !chip.classList.contains("fill-blank-chip--correct")) {
          this.bankTarget.appendChild(chip);
        }
      });
    }
  }

  _placeChipInSlot(chip, dropZone) {
    dropZone.appendChild(chip);
  }

  _returnChipToBank(chip, slot) {
    chip.classList.remove(
      "fill-blank-chip--correct",
      "fill-blank-chip--wrong",
      "fill-blank-chip--selected",
      "fill-blank-chip--placed",
    );
    chip.removeAttribute("aria-pressed");
    chip.setAttribute("data-action", "click->word-image-match-player#chipClicked keydown->word-image-match-player#chipKeydown");
    chip.setAttribute("data-word-image-match-player-target", "chip");
    chip.setAttribute("role", "button");
    chip.setAttribute("tabindex", "0");
    chip.setAttribute("aria-label", chip.textContent.trim());
    this.bankTarget.appendChild(chip);
    if (slot) {
      slot.dataset.state = "empty";
      slot.classList.remove("ring-2", "ring-success", "ring-error");
      const dz = this._dropZoneOf(slot);
      if (dz) {
        dz.classList.remove("fill-blank-drop-zone--correct", "fill-blank-drop-zone--wrong");
        dz.classList.add("fill-blank-drop-zone--empty");
      }
      const indicator = slot.querySelector(".absolute.top-1.right-1");
      if (indicator) indicator.remove();
    }
  }

  _deselectChip() {
    if (!this.selectedChip) return;
    this.selectedChip.classList.remove("fill-blank-chip--selected");
    this.selectedChip.setAttribute("aria-pressed", "false");
    if (this.selectedChip.dataset.originalLabel) {
      this.selectedChip.setAttribute("aria-label", this.selectedChip.dataset.originalLabel);
    }
    this.selectedChip = null;
  }

  _afterMove() {
    this._refreshState();
    this._scheduleAutosave();
  }

  _refreshState() {
    if (!this.hasCheckButtonTarget) return;
    const allFilled = this.slotTargets.every((s) => {
      if (s.dataset.locked === "true") return true;
      return !!this._slotChip(s);
    });
    const someFilled = this.slotTargets.some((s) => this._slotChip(s));
    this.checkButtonTarget.disabled = !allFilled || this.checking || !someFilled;
  }

  _currentPlacements() {
    const placements = {};
    this.slotTargets.forEach((slot) => {
      const chip = this._slotChip(slot);
      placements[slot.dataset.itemId] = chip ? chip.textContent.trim() : "";
    });
    return placements;
  }

  _scheduleAutosave() {
    if (this.checking || !this.hasDraftUrlValue) return;
    autosaveDraft({
      url: this.draftUrlValue,
      body: { submitted: this._currentPlacements() },
    });
  }

  check(event) {
    event?.preventDefault();
    if (this.checking) return;
    if (this.checkButtonTarget.disabled) return;

    this.checking = true;
    this.checkButtonTarget.disabled = true;
    if (this.hasDraftUrlValue) cancelAutosave(this.draftUrlValue);

    const placements = this._currentPlacements();
    const token = document.querySelector("meta[name=csrf-token]")?.content || "";
    const params = new URLSearchParams();
    Object.entries(placements).forEach(([id, w]) => {
      params.append(`submitted[${id}]`, w);
    });

    fetch(this.checkUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Accept: "text/vnd.turbo-stream.html",
        "X-CSRF-Token": token,
      },
      body: params.toString(),
    })
      .then((r) => {
        if (handleStaleResponse(this, r)) return null;
        return r.text();
      })
      .then((html) => {
        if (html) Turbo.renderStreamMessage(html);
      })
      .finally(() => {
        this.checking = false;
      });
  }

  // Helpers
  _slotChip(slot) {
    return slot.querySelector(".fill-blank-chip");
  }

  _dropZoneOf(slot) {
    return slot.querySelector("[data-word-image-match-player-target='dropZone']");
  }
}
